import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CraditService } from './cradit.service';

/**
 * Reporting-period settings captured once in the Financial section (typ = 1)
 * and reused by every other financial section (Income Statement, Extra Terms)
 * instead of asking for them again.
 */
export interface FinancialProfile {
  /** 0 = PKR, 1 = USD */
  reportingCurrency: number | null;
  /** yyyy-MM-dd, or '' */
  financialYearEnd: string;
  isConsolidated: boolean;
  /** Newest first. */
  years: string[];
  /** fnclType per year: 0 = Audited, 1 = Management / Unaudited. */
  typeByYear: Record<string, number | null>;
}

/** The Financial section's `typ` on `/get-fncl-dta/{crdtPrpslSeq}/{typ}`. */
export const FINANCIAL_SECTION_TYPE = 1;

function normalizeStatementType(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const type = Number(value);
  return type === 0 || type === 1 ? type : null;
}

/**
 * Builds the profile from a `get-fncl-dta` response for the Financial section.
 * Returns null until that section has saved at least one year.
 */
export function toFinancialProfile(response: unknown): FinancialProfile | null {
  const data: any = Array.isArray(response)
    ? response[0]
    : Array.isArray((response as any)?.data)
      ? (response as any).data[0]
      : ((response as any)?.data ?? response);

  const records: any[] = (Array.isArray(data?.crdtFnclDtaLst) ? data.crdtFnclDtaLst : []).filter(
    (record: any) => Number(record?.delFlg ?? 0) !== 1 && record?.fncYr !== null && record?.fncYr !== undefined,
  );

  if (!records.length) {
    return null;
  }

  const years = [...new Set(records.map((record) => String(record.fncYr)))].sort(
    (first, second) => Number(second) - Number(first),
  );

  const typeByYear: Record<string, number | null> = {};

  years.forEach((year) => {
    const saved = records
      .filter((record) => String(record.fncYr) === year)
      .map((record) => normalizeStatementType(record.fnclType))
      .find((type) => type !== null);

    typeByYear[year] = saved ?? null;
  });

  const currency = data?.rprtngCrncy;

  return {
    reportingCurrency: currency === null || currency === undefined || currency === '' ? null : Number(currency),
    financialYearEnd: data?.fnclYrEnd ? String(data.fnclYrEnd).slice(0, 10) : '',
    isConsolidated: Number(data?.cnsldtdFncl) === 1,
    years,
    typeByYear,
  };
}

@Injectable({ providedIn: 'root' })
export class FinancialProfileService {
  private readonly changedSubject = new Subject<void>();

  /** Emits after the Financial section saves, so dependent sections reload. */
  readonly changed$ = this.changedSubject.asObservable();

  constructor(private readonly creditService: CraditService) {}

  load(crdtPrpslSeq: number | null | undefined): Observable<FinancialProfile | null> {
    const sequence = Number(crdtPrpslSeq ?? 0);

    if (!Number.isFinite(sequence) || sequence <= 0) {
      return of(null);
    }

    return this.creditService.getFinancialData(sequence, FINANCIAL_SECTION_TYPE).pipe(
      map((response) => toFinancialProfile(response)),
      catchError((error) => {
        console.error('Unable to load the Financial section settings:', error);
        return of(null);
      }),
    );
  }

  notifyChanged(): void {
    this.changedSubject.next();
  }
}
